import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { AlertService } from '@/app/atoms/alert/alert.service';
import {
  AssociatedCreativeBriefTemplate,
  CreatedCreativeBriefTemplate,
  CreativeBriefTemplate,
  CreateBriefTemplateDto,
} from '@/app/services/creative-brief-template/creative-brief-template';
import { WysiswygComponent } from '@/app/atoms/wysiswyg/wysiswyg.component';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Injector,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormField, form, minLength, required } from '@angular/forms/signals';
import { firstValueFrom } from 'rxjs';
import { LoadingInlineComponent } from '../loading-inline/loading-inline.component';

@Component({
  selector: 'atom-brief-template-drawer',
  imports: [LoadingInlineComponent, WysiswygComponent, FormField],
  templateUrl: './brief-template-drawer.html',
  styleUrl: './brief-template-drawer.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class BriefTemplateDrawer implements OnInit {
  @ViewChild('briefTemplateDrawerDialog', { static: false })
  briefTemplateDrawerDialog?: ElementRef<HTMLDialogElement>;

  @ViewChild('keyWysiwyg') private keyWysiwyg?: WysiswygComponent;
  @ViewChild('doWysiwyg') private doWysiwyg?: WysiswygComponent;
  @ViewChild('dontWysiwyg') private dontWysiwyg?: WysiswygComponent;

  /** Emits the API response after a template is created (parent can sync brief fields, etc.). */
  readonly templateCreated = output<CreatedCreativeBriefTemplate>();

  /** Emits when the user applies an existing template (same shape as create for parent handlers). */
  readonly templateSelected = output<CreatedCreativeBriefTemplate>();

  /** Existing or New Templates */
  currentTab = signal<'existing' | 'new'>('existing');

  private creativeBriefTemplateService = inject(CreativeBriefTemplate);
  private currentUserService = inject(CurrentUserService);
  private alertService = inject(AlertService);
  private injector = inject(Injector);
  private host = inject(ElementRef<HTMLElement>);

  associatedTemplates = signal<AssociatedCreativeBriefTemplate[] | undefined>(undefined);

  /** When set, save performs PUT instead of POST. */
  private editingTemplateId = signal<string | null>(null);
  /** Preserves JSON fields not shown in the form while editing. */
  private editingTemplateSnapshot = signal<AssociatedCreativeBriefTemplate | null>(null);

  /** Which template row has the options menu open (id). */
  private optionsMenuOpenForId = signal<string | null>(null);

  newTemplateModel = signal({
    templateName: '',
  });

  newTemplateForm = form(this.newTemplateModel, (schemaPath) => {
    required(schemaPath.templateName, { message: 'Template name is required' });
    minLength(schemaPath.templateName, 2, {
      message: 'Template name must be at least 2 characters',
    });
  });

  keyMessagingHtml = signal('');
  mandatoryItemsHtml = signal('');
  restrictionsHtml = signal('');

  saving = signal(false);
  saveAttempted = signal(false);
  saveFeedback = signal<{ kind: 'error' | 'success'; message: string } | null>(null);

  /** Bumps when the form resets so WYSIWYG editors remount with empty content. */
  wysiwygSession = signal(0);

  saveActionLabel = computed(() =>
    this.editingTemplateId() ? 'Update Template' : 'Save Template'
  );

  ngOnInit(): void {
    this.getAssociatedTemplates();
  }

  getAssociatedTemplates(): void {
    this.creativeBriefTemplateService
      .getAssociatedTemplates(
        this.currentUserService.focussedBrand.id,
        this.currentUserService.focussedAgency?.agency?.id || undefined
      )
      .subscribe((templates) => {
        this.associatedTemplates.set(templates);
      });
  }

  switchToNew(tab: 'existing' | 'new'): void {
    this.currentTab.set(tab);
    if (tab === 'new') {
      this.saveFeedback.set(null);
      this.saveAttempted.set(false);
      this.editingTemplateId.set(null);
      this.editingTemplateSnapshot.set(null);
      this.newTemplateModel.set({ templateName: '' });
      this.keyMessagingHtml.set('');
      this.mandatoryItemsHtml.set('');
      this.restrictionsHtml.set('');
      this.wysiwygSession.update((n) => n + 1);
    }
  }

  openDrawer(): void {
    const dialog = this.briefTemplateDrawerDialog?.nativeElement;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  }

  closeDrawer(): void {
    this.optionsMenuOpenForId.set(null);
    this.briefTemplateDrawerDialog?.nativeElement?.close();
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.optionsMenuOpenForId()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target || !this.host.nativeElement.contains(target)) {
      return;
    }
    if (target.closest('[data-brief-template-options]')) {
      return;
    }
    this.optionsMenuOpenForId.set(null);
  }

  getTabClass(tab: 'existing' | 'new'): string {
    return this.currentTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  formatTemplateCreatedDate(template: AssociatedCreativeBriefTemplate): string {
    const raw = template.createdDate;
    if (!raw) {
      return '';
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(d);
  }

  private toCreatedShape(t: AssociatedCreativeBriefTemplate): CreatedCreativeBriefTemplate {
    const stepsHtml =
      (t.stepsHtml?.trim().length ? t.stepsHtml : t.creativeBriefContent) ?? '';
    return {
      id: t.id,
      businessAccountId: t.businessAccountId ?? this.currentUserService.focussedBrand.id ?? '',
      agencyId: t.agencyId ?? this.currentUserService.focussedAgency?.agency?.id ?? '',
      creativeBriefTitle: t.creativeBriefTitle,
      creativeBriefContent: t.creativeBriefContent ?? '',
      stepsHtml,
      doHtml: t.doHtml ?? '',
      dontHtml: t.dontHtml ?? '',
      stepsPlainText: t.stepsPlainText ?? '',
      doPlainText: t.doPlainText ?? '',
      dontPlainText: t.dontPlainText ?? '',
      creativeConcepts: t.creativeConcepts ?? [],
      contentPillars: t.contentPillars ?? [],
    };
  }

  useTemplate(template: AssociatedCreativeBriefTemplate): void {
    this.optionsMenuOpenForId.set(null);
    this.templateSelected.emit(this.toCreatedShape(template));
    this.closeDrawer();
  }

  isTemplateOptionsOpen(templateId: string): boolean {
    return this.optionsMenuOpenForId() === templateId;
  }

  toggleTemplateOptionsMenu(templateId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.optionsMenuOpenForId.update((id) => (id === templateId ? null : templateId));
  }

  beginEditTemplate(template: AssociatedCreativeBriefTemplate, event: MouseEvent): void {
    event.stopPropagation();
    this.optionsMenuOpenForId.set(null);
    this.editingTemplateId.set(template.id);
    this.editingTemplateSnapshot.set(template);

    const steps =
      (template.stepsHtml?.trim().length ? template.stepsHtml : template.creativeBriefContent) ??
      '';
    this.newTemplateModel.set({ templateName: template.creativeBriefTitle });
    this.keyMessagingHtml.set(steps);
    this.mandatoryItemsHtml.set(template.doHtml ?? '');
    this.restrictionsHtml.set(template.dontHtml ?? '');
    this.saveFeedback.set(null);
    this.saveAttempted.set(false);
    this.currentTab.set('new');
    this.wysiwygSession.update((n) => n + 1);

    afterNextRender(
      () => {
        this.keyWysiwyg?.applyExternalHtml(this.keyMessagingHtml());
        this.doWysiwyg?.applyExternalHtml(this.mandatoryItemsHtml());
        this.dontWysiwyg?.applyExternalHtml(this.restrictionsHtml());
      },
      { injector: this.injector }
    );
  }

  async confirmDeleteTemplate(
    template: AssociatedCreativeBriefTemplate,
    event: MouseEvent
  ): Promise<void> {
    event.stopPropagation();
    this.optionsMenuOpenForId.set(null);

    const confirmed = await this.alertService.confirm({
      title: 'Delete template',
      message: `Are you sure you want to delete "${template.creativeBriefTitle}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) {
      return;
    }

    try {
      await firstValueFrom(this.creativeBriefTemplateService.deleteTemplate(template.id));
      this.getAssociatedTemplates();
    } catch {
      await this.alertService.alert({
        title: 'Could not delete',
        message: 'Something went wrong. Please try again.',
        confirmText: 'OK',
        cancelText: 'Cancel',
        type: 'alert',
        icon: 'error',
      });
    }
  }

  onKeyMessagingHtml(html: string): void {
    this.keyMessagingHtml.set(html);
  }

  onMandatoryItemsHtml(html: string): void {
    this.mandatoryItemsHtml.set(html);
  }

  onRestrictionsHtml(html: string): void {
    this.restrictionsHtml.set(html);
  }

  private stripHtmlToText(html: string): string {
    return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }

  private incompleteForm(emptyFields: string[]): void {
    const list = emptyFields.join(', ');
    this.saveFeedback.set({
      kind: 'error',
      message: `Please complete the following: ${list}.`,
    });
  }

  async saveTemplate(): Promise<void> {
    this.saveAttempted.set(true);
    this.saveFeedback.set(null);

    this.keyWysiwyg?.flushHtmlToParent();
    this.doWysiwyg?.flushHtmlToParent();
    this.dontWysiwyg?.flushHtmlToParent();

    const emptyFields: string[] = [];
    const trimmedName = this.newTemplateModel().templateName.trim();

    if (!trimmedName || this.newTemplateForm.templateName().invalid()) {
      emptyFields.push('Template Name');
    }

    const keyPlain = this.stripHtmlToText(this.keyMessagingHtml());
    const doPlain = this.stripHtmlToText(this.mandatoryItemsHtml());
    const dontPlain = this.stripHtmlToText(this.restrictionsHtml());

    if (!keyPlain) {
      emptyFields.push('Key Message');
    }
    if (!doPlain) {
      emptyFields.push("Do's");
    }
    if (!dontPlain) {
      emptyFields.push("Don'ts");
    }

    if (emptyFields.length > 0) {
      this.incompleteForm(emptyFields);
      return;
    }

    const snapshot = this.editingTemplateSnapshot();
    const payload: CreateBriefTemplateDto = {
      businessAccountId: this.currentUserService.focussedBrand.id!,
      agencyId: this.currentUserService.focussedAgency?.agency?.id || '',
      creativeBriefTitle: trimmedName,
      creativeBriefContent: this.keyMessagingHtml(),
      stepsHtml: this.keyMessagingHtml(),
      doHtml: this.mandatoryItemsHtml(),
      dontHtml: this.restrictionsHtml(),
      stepsPlainText: keyPlain,
      doPlainText: doPlain,
      dontPlainText: dontPlain,
      creativeConcepts: snapshot?.creativeConcepts ?? [],
      contentPillars: snapshot?.contentPillars ?? [],
    };

    const editId = this.editingTemplateId();
    this.saving.set(true);
    try {
      if (editId) {
        await firstValueFrom(
          this.creativeBriefTemplateService.updateCreativeBriefTemplate(editId, payload)
        );
        const updated: CreatedCreativeBriefTemplate = { ...payload, id: editId };
        this.templateCreated.emit(updated);
        this.saveFeedback.set({
          kind: 'success',
          message: 'Template updated!',
        });
      } else {
        const created = await firstValueFrom(
          this.creativeBriefTemplateService.createCreativeBriefTemplate(payload)
        );
        this.templateCreated.emit(created);
        this.saveFeedback.set({
          kind: 'success',
          message: 'Template Saved!',
        });
      }

      this.editingTemplateId.set(null);
      this.editingTemplateSnapshot.set(null);
      this.newTemplateModel.set({ templateName: '' });
      this.keyMessagingHtml.set('');
      this.mandatoryItemsHtml.set('');
      this.restrictionsHtml.set('');
      this.saveAttempted.set(false);
      this.wysiwygSession.update((n) => n + 1);
      this.getAssociatedTemplates();
      this.currentTab.set('existing');
    } catch {
      this.saveFeedback.set({
        kind: 'error',
        message: editId ? 'Failed to update template' : 'Failed to save template',
      });
    } finally {
      this.saving.set(false);
    }
  }
}
